/* Invoice tab filters — country (major list + others in report) and service
 * multi-select; re-tallies each invoice to matching parcels only. */
import { useEffect, useState } from "react";
import { ChevronDown, Globe, RotateCcw, Search } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Input } from "./ui/input";

export const MAJOR_COUNTRIES = ["USA", "UK", "Canada", "Australia", "Germany", "France", "Netherlands", "Italy",
  "Spain", "Ireland", "New Zealand", "Switzerland", "Sweden", "Belgium", "Norway", "Israel", "UAE", "Singapore",
  "Japan", "Hong Kong"];
const EMPTY = { countries: [], services: [] };
const key = (user) => `reportInvoiceFilters:${user?.email || user?.id || "me"}`;

export const useInvoiceFilters = (user) => {
  const [f, setF] = useState(() => {
    try { return { ...EMPTY, ...JSON.parse(localStorage.getItem(key(user)) || "{}") }; } catch { return EMPTY; }
  });
  useEffect(() => { localStorage.setItem(key(user), JSON.stringify(f)); }, [user, f]);
  return [f, setF, () => setF(EMPTY)];
};

export const isFiltered = (f) => !!(f.countries.length || f.services.length);

export const filterInvoices = (data, f) => {
  const rows = data?.invoice_data || [];
  if (!isFiltered(f)) return rows;
  return rows.map(r => {
    const parts = (r.breakdown || []).filter(b =>
      (!f.countries.length || f.countries.includes(b.country)) && (!f.services.length || f.services.includes(b.service)));
    if (!parts.length) return null;
    const row = { ...r, cancelled: 0, total: parts.reduce((n, b) => n + b.n, 0),
      weight: Math.round(parts.reduce((n, b) => n + b.w, 0) * 100) / 100,
      weighed_parcels: parts.reduce((n, b) => n + b.wn, 0),
      countries: [...new Set(parts.map(b => b.country))].sort().join(", ") };
    (data.services || []).forEach(s => { row[s] = parts.filter(b => b.service === s).reduce((n, b) => n + b.n, 0); });
    return row;
  }).filter(Boolean);
};

const countryCounts = (data) => {
  const m = {};
  (data?.invoice_data || []).forEach(r => (r.breakdown || []).forEach(b => { m[b.country] = (m[b.country] || 0) + b.n; }));
  return m;
};

const CountryPicker = ({ data, value, onChange }) => {
  const [q, setQ] = useState("");
  const counts = countryCounts(data);
  const others = Object.keys(counts).filter(c => !MAJOR_COUNTRIES.includes(c)).sort((a, b) => counts[b] - counts[a]);
  const match = (c) => !q.trim() || c.toLowerCase().includes(q.trim().toLowerCase());
  const toggle = (c) => onChange(value.includes(c) ? value.filter(x => x !== c) : [...value, c]);
  const item = (c) => (
    <label key={c} className={`flex items-center gap-2 px-2 py-1 rounded text-xs cursor-pointer hover:bg-slate-50 ${counts[c] ? "" : "text-slate-400"}`}
           data-testid={`invoice-filter-country-opt-${c.replace(/\s+/g, "-")}`}>
      <input type="checkbox" checked={value.includes(c)} onChange={() => toggle(c)} />
      <span className="flex-1">{c}</span>
      <span className="text-[10px] text-slate-400">{counts[c] || 0}</span>
    </label>);
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button type="button" data-testid="invoice-filter-country-btn"
                className={`h-8 inline-flex items-center gap-1.5 px-3 rounded-md border text-xs font-medium transition-colors ${value.length
                  ? "bg-blue-600 text-white border-blue-600" : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"}`}>
          <Globe className="w-3.5 h-3.5" />
          {value.length ? (value.length <= 2 ? value.join(", ") : `${value.length} countries`) : "All countries"}
          <ChevronDown className="w-3 h-3 opacity-70" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-64 p-2" data-testid="invoice-filter-country-menu">
        <div className="relative mb-2">
          <Search className="w-3 h-3 absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Search country" className="h-7 pl-7 text-xs"
                 data-testid="invoice-filter-country-search" />
        </div>
        <div className="max-h-72 overflow-y-auto">
          <p className="px-2 pt-1 text-[10px] font-semibold uppercase tracking-wide text-slate-400">Major countries</p>
          {MAJOR_COUNTRIES.filter(match).map(item)}
          {others.filter(match).length > 0 && <p className="px-2 pt-2 text-[10px] font-semibold uppercase tracking-wide text-slate-400">Other countries in this report</p>}
          {others.filter(match).map(item)}
        </div>
        {value.length > 0 && (
          <button type="button" onClick={() => onChange([])} className="mt-2 w-full text-[11px] text-blue-700 hover:underline"
                  data-testid="invoice-filter-country-clear">Clear countries</button>)}
      </PopoverContent>
    </Popover>
  );
};

export const InvoiceFilterBar = ({ data, filters, setFilters, reset, shown, total }) => {
  const toggleSvc = (s) => setFilters(f => ({ ...f, services: f.services.includes(s) ? f.services.filter(x => x !== s) : [...f.services, s] }));
  return (
    <div className="no-print mb-3 flex flex-wrap items-center gap-2" data-testid="invoice-filter-bar">
      <CountryPicker data={data} value={filters.countries} onChange={(countries) => setFilters(f => ({ ...f, countries }))} />
      <span className="text-[11px] font-semibold text-slate-500 ml-1">Service</span>
      {(data.services || []).map(s => (
        <button key={s} type="button" onClick={() => toggleSvc(s)} data-testid={`invoice-filter-service-${s}`}
                className={`h-7 px-2.5 rounded-full border text-[11px] font-semibold transition-colors ${filters.services.includes(s)
                  ? "bg-blue-600 text-white border-blue-600" : "bg-white text-slate-600 border-slate-300 hover:bg-slate-50"}`}>
          {s}
        </button>))}
      {isFiltered(filters) && (
        <>
          <button type="button" onClick={reset} data-testid="invoice-filter-reset"
                  className="h-7 inline-flex items-center gap-1 px-2.5 rounded-full text-[11px] font-semibold text-rose-700 border border-rose-300 hover:bg-rose-50">
            <RotateCcw className="w-3 h-3" />Reset
          </button>
          <span className="text-xs text-slate-500" data-testid="invoice-filter-count">{shown} of {total} invoices</span>
        </>)}
    </div>
  );
};
